import { themeColors } from "../../../themeColors";
/**
 * @file   src\mobile\components\Settings\PaymentComponent.js
 * @brief  This file is responsible for various actions on payment methods like delete, set default payment ,method
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import "../../styles/clients.scss";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  classCardNumber,
} from "@stripe/react-stripe-js";
import { savePaymentDetails } from "../../../services/paymentsService";
import {
  getCardElementOptions,
  getUserId,
  toTitleCase,
} from "../../../helpers/common";
import TextBox from "../../../components/Common/TextBox";
import { fetchPaymentMethods } from "../../../actions/payments";
import {
  EDIT_PAYMENT,
  PAYMENT_LIST,
  REVIEW_ORDER,
} from "../../../constants/onBoarding";
import TopHeader from "../common/TopHeader";
import { usePremium } from "@/hooks";
import BackLinkButton from "../../components/BackLinkButton";

const CardInputWrapper = styled.div`
  border: 2px solid #cfd4dd;
  border-radius: 12px;
  padding: 12px;
  height: 47px;
`;

const PaymentComponent = ({ ...props }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = getUserId();
  const paymentMethods = useSelector(
    (state) => state.paymentMethods.paymentMethods,
  );
  const [cardSave, setCardSave] = useState(false);
  const [name, setName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [nameError, setNameError] = useState("");
  const [zipCodeError, setZipCodeError] = useState("");
  const [errorMessages, setErrorMessages] = useState({
    cardNumberError: "",
    cvvError: "",
    expiryError: "",
  });

  const [cardNumberComplete, setCardNumberCOmplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCVCComplete, setCardCVCComplete] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const { shouldToggleColor } = usePremium();

  const re = /^[A-Za-z0-9 -]+$/;
  const handleNameChange = (value) => {
    setNameError("");
    if (value === "" || re.test(value)) {
      setName(value);
    }
    if (value.trim() === "") {
      setNameError("Name on card is incomplete");
    }
  };

  const handleZipCodeChange = (value) => {
    setZipCodeError("");
    if (value === "" || re.test(value)) {
      setZipCode(value);
    }
    if (value.trim() === "") {
      setZipCodeError("ZipCode is incomplete");
    }
  };
  /**
   * To handle caed number field changes
   * @param {*} e
   */
  const handleCardNumberChange = (e) => {
    setErrorMessages({ ...errorMessages, cardNumberError: "" });
    if (e.complete) {
      setCardNumberCOmplete(true);
    } else {
      setCardNumberCOmplete(false);
    }
    if (e?.error?.message !== "") {
      setErrorMessages({
        ...errorMessages,
        cardNumberError: e?.error?.message,
      });
    }
  };
  /**
   * To handle card expiry field
   * @param {*} e
   */
  const handleCardExpiryChange = (e) => {
    setErrorMessages({ ...errorMessages, expiryError: "" });
    if (e.complete) {
      setCardExpiryComplete(true);
    } else {
      setCardExpiryComplete(false);
    }
    if (e?.error?.message !== "") {
      setErrorMessages({ ...errorMessages, expiryError: e?.error?.message });
    }
  };

  /**
   * To handle cvv field changes
   * @param {*} e
   */
  const handleCVCChange = (e) => {
    setErrorMessages({ ...errorMessages, cvvError: "" });
    if (e.complete) {
      setCardCVCComplete(true);
    } else {
      setCardCVCComplete(false);
    }
    if (e?.error?.message !== "") {
      setErrorMessages({ ...errorMessages, cvvError: e?.error?.message });
    }
  };
  /**
   * To validate entries
   * @returns
   */
  const checkValid = () => {
    if (
      name !== "" &&
      zipCode !== "" &&
      cardNumberComplete &&
      cardCVCComplete &&
      cardExpiryComplete &&
      !submitClicked
    ) {
      return false;
    } else {
      return true;
    }
  };
  /**
   * To handle add payment method
   * @param {*} e
   * @returns
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitClicked(true);
    if (!stripe && !elements) {
      return;
    }

    const cardNumberElement = elements.getElement(
      "cardNumber",
      "cardExpiry",
      "cardCvc",
    );
    const { token, error } = await stripe.createToken(cardNumberElement, {
      name: name,
      address_zip: zipCode,
    });
    if (error) {
      setCardSave(false);
      toast.error(error.message);
    } else {
      if (token?.id) {
        const data = {
          Name: name,
          ZipCode: zipCode,
          TokenId: token?.id,
          CardId: token?.card?.id,
          Brand: token?.card?.brand,
          CVC_Check: token?.card?.cvc_check,
          Exp_month: token?.card?.exp_month,
          Exp_year: token?.card?.exp_year,
          Funding: token?.card?.funding,
          Last4: token.card.last4,
          IsDefault: paymentMethods?.length > 0 ? 0 : 1,
        };

        await savePaymentDetails(data)
          .then((response) => {
            if (response.errorCode === 0) {
              toast.success("Payment method successfully added!");
              if (userId) {
                dispatch(fetchPaymentMethods());
              }
              setSubmitClicked(false);
              if (props.isFromSendIntroductionPlan) {
                props.handlePageToggle({ activePage: REVIEW_ORDER });
              } else if (props.isFromReviewPlan) {
                props.handlePageToggle({ activePage: EDIT_PAYMENT });
              } else {
                navigate(pageURLs.mobilePaymentHistory);
              }
            } else {
              toast.error(response.message);
              setSubmitClicked(false);
            }
          })
          .catch((err) => {
            toast.error(err);
          });
      }
    }
  };

  useEffect(() => {
    document.body.classList.add("bg-offcanvas-fix");
  }, []);

  return (
    <div className="gitcha-main-wrapper view-client-wrap select-payment-wrap pb-102">
      <div className="content-wrapper content-wrapper-h-102-percent">
        <TopHeader
          variant={"review"}
          outerClassname="bg-white"
          topClassName="header-brdr-btm w-100"
          bottomClassName="signup-header-txt clearfix"
        >
          <BackLinkButton
            variant="small"
            shouldToggleColor={shouldToggleColor}
            role="button"
            onClick={() =>
              props.isFromSendIntroductionPlan
                ? props.handlePageToggle({ activePage: PAYMENT_LIST })
                : props.isFromReviewPlan
                  ? props.handlePageToggle({ activePage: EDIT_PAYMENT })
                  : navigate(pageURLs.mobilePaymentHistory)
            }
          />
          <span className={shouldToggleColor ? "heading-premium" : "heading"}>
            Add Payment Method
          </span>
        </TopHeader>
        <div className="inner-layout-content-new">
          <div className="payment-form">
            <div className="mob-fld-wrap position-relative field-spacer">
              <label className="form-label">Name on Card</label>
              <TextBox
                id="cardname"
                name="cardname"
                type="text"
                maxLength={50}
                value={toTitleCase(name)}
                placeholder="First and Last Name"
                onChange={(e) => handleNameChange(e.target.value)}
                className="form-control mob-pr-44"
                errorClass="error-validation-msg h-10"
                errorMessage={nameError}
              />
            </div>
            <div className="mob-fld-wrap position-relative field-spacer">
              <label className="form-label">Card Number</label>

              <CardInputWrapper className="stripe-field mb-0">
                <CardNumberElement
                  onChange={(e) => handleCardNumberChange(e)}
                  placeholder="Custom card number placeholder"
                  className={"stripe-control " + classCardNumber}
                  options={getCardElementOptions("••••••••••••0000")}
                />
              </CardInputWrapper>
              {
                <div className="mob-error-msg">
                  {errorMessages?.cardNumberError}
                </div>
              }
            </div>
            <div className="field-group d-grid">
              <div className="mob-fld-wrap position-relative">
                <label className="form-label">Exp Date</label>

                <CardInputWrapper className="stripe-field mb-0">
                  <CardExpiryElement
                    onChange={(e) => handleCardExpiryChange(e)}
                    className={"stripe-control " + classCardNumber}
                    options={getCardElementOptions("MM/YY")}
                  />
                </CardInputWrapper>
              </div>
              <div className="mob-fld-wrap position-relative">
                <label className="form-label">CVC</label>

                <CardInputWrapper className="stripe-field mb-0">
                  <CardCvcElement
                    onChange={(e) => handleCVCChange(e)}
                    className={"stripe-control " + classCardNumber}
                    options={getCardElementOptions("123")}
                  />
                </CardInputWrapper>
              </div>
            </div>
            <div className="mob-fld-wrap position-relative field-spacer">
              <label className="form-label">ZIP Code</label>
              <TextBox
                id="zipcode"
                name="zipcode"
                type="text"
                maxLength={10}
                value={zipCode}
                onChange={(e) => handleZipCodeChange(e.target.value)}
                className="form-control"
                errorClass="error-validation-msg h-10"
                errorMessage={zipCodeError}
                placeholder="12345"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="inner-layout-footer-new">
        <div className="inner-btm-fixed-nav">
          <div className="btn-wrap">
            <button
              type="button"
              className="btn btn-primary w-100"
              disabled={checkValid()}
              onClick={(e) => handleSubmit(e)}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentComponent;
